import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Grid, Button, TextField, CircularProgress, Box, Paper, Typography } from '@mui/material';
import { useQuery } from 'react-query';
import momentTZ from 'moment-timezone';

import { useWorkplacesContext } from 'module/workplaces/context/WorkplacesContext';
import {
	fetchSingleEvent,
	fetchAllEventTypes,
	fetchAllLocations,
	getEventDocumentByReference,
} from 'core/API/events';
import { getJobCodes } from 'core/API/scheduler';
import { fetchAllCustomers } from 'core/API/customers';
import styles from './CreateEvent.module.scss';

const EventDetails: React.FC = () => {
	const history = useHistory();
	const location = useLocation();
	const { workplaceId, timezone } = useWorkplacesContext();

	const [documentLinks, setDocumentLinks] = useState<Array<{ ref: string; url: string; label: string }>>([]);
	const [documentsLoading, setDocumentsLoading] = useState(false);

	// Get event data from location state or params
	const locationState = location.state as any;
	const eventFromState = locationState?.event;
	const resolveEventId = (value: any): number | null => {
		if (value === null || value === undefined) return null;
		if (typeof value === 'number') return Number.isFinite(value) ? value : null;
		if (typeof value === 'string') {
			const match = value.match(/(\d+)(?:\/)?$/);
			if (!match) return null;
			const parsed = Number(match[1]);
			return Number.isFinite(parsed) ? parsed : null;
		}
		if (typeof value === 'object') {
			if (value.id !== undefined && value.id !== null) return resolveEventId(value.id);
			if (value['@id']) return resolveEventId(value['@id']);
		}
		return null;
	};
	const eventIdFromState =
		resolveEventId(locationState?.eventId) ||
		resolveEventId(eventFromState?.id) ||
		resolveEventId(eventFromState?.['@id']) ||
		null;

	const { data: fetchedEvent, isLoading: isLoadingEvent } = useQuery(
		['event', eventIdFromState],
		() => fetchSingleEvent(eventIdFromState),
		{
			enabled: !!eventIdFromState,
			initialData: eventFromState,
			staleTime: 0,
			refetchOnMount: 'always',
		}
	);

	const eventData = fetchedEvent || eventFromState;

	type IriLike = string | number | null | undefined | { id?: number | string; '@id'?: string };

	const extractIdFromIri = (value: IriLike): number => {
		if (value === null || value === undefined) return 0;
		if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
		if (typeof value === 'object') {
			const directId = (value as any).id;
			if (directId !== undefined && directId !== null) return extractIdFromIri(directId);
			const atId = (value as any)['@id'];
			if (atId) return extractIdFromIri(atId);
			return 0;
		}
		if (typeof value !== 'string') return 0;
		const tail = value.split('/').pop() || value;
		const id = parseInt(tail, 10);
		return Number.isNaN(id) ? 0 : id;
	};

	const normalizeDateInput = (value: any): Date | null => {
		if (!value) return null;
		const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
		return Number.isNaN(date.getTime()) ? null : date;
	};

	const parseTimeParts = (time?: string | null) => {
		if (!time) return null;
		const [hoursStr, minutesStr, secondsStr] = String(time).split(':');
		const hours = Number(hoursStr);
		const minutes = Number(minutesStr ?? '0');
		const seconds = Number(secondsStr ?? '0');
		if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) return null;
		return { hours, minutes, seconds };
	};

	const combineDateAndTime = (dateValue: any, timeValue?: string | null): Date | null => {
		const date = normalizeDateInput(dateValue);
		if (!date) return null;
		const timeParts = parseTimeParts(timeValue);
		if (timeParts) {
			const { hours, minutes, seconds } = timeParts;
			date.setHours(hours, minutes, seconds ?? 0, 0);
		}
		return date;
	};

	const eventTypeId = eventData?.type ? extractIdFromIri(eventData.type) : 0;
	const locationId = eventData?.location ? extractIdFromIri(eventData.location) : 0;
	const customerId = eventData?.customer ? extractIdFromIri(eventData.customer) : 0;

	const { data: eventTypes } = useQuery(
		['eventTypes', workplaceId],
		() => fetchAllEventTypes(workplaceId),
		{
			enabled: !!workplaceId,
			staleTime: Infinity,
		}
	);

	const { data: customers } = useQuery(
		['customers'],
		() => fetchAllCustomers(),
		{
			staleTime: Infinity,
		}
	);

	const { data: jobCodesData } = useQuery(
		['jobCodes'],
		() => getJobCodes(),
		{
			staleTime: Infinity,
		}
	);

	const { data: locations } = useQuery(
		['locations', workplaceId],
		() => fetchAllLocations(workplaceId),
		{
			enabled: !!workplaceId,
			staleTime: Infinity,
		}
	);

	// Sort job codes by job number
	const sortedJobCodesData = useMemo(() => {
		if (!jobCodesData) return [];
		return [...jobCodesData].sort((a, b) => {
			const aNum = a.jobNumber ?? 999;
			const bNum = b.jobNumber ?? 999;
			return aNum - bNum;
		});
	}, [jobCodesData]);

	const selectedEventType = useMemo(() => {
		if (!eventTypes?.member || !eventTypeId) return null;
		return eventTypes.member.find((et: any) => et.id === eventTypeId);
	}, [eventTypes, eventTypeId]);

	const selectedLocation = useMemo(() => {
		if (!locations?.member || !locationId) return null;
		return locations.member.find((loc: any) => loc.id === locationId);
	}, [locations, locationId]);

	const selectedCustomer = useMemo(() => {
		if (!customers?.member || !customerId) return null;
		return customers.member.find((c: any) => c.id === customerId);
	}, [customers, customerId]);

	const customerDisplayName =
		selectedCustomer?.fullName ||
		(selectedCustomer?.firstName && selectedCustomer?.lastName
			? `${selectedCustomer.firstName} ${selectedCustomer.lastName}`
			: selectedCustomer?.name || '');

	const locationDisplayValue =
		selectedLocation?.label ||
		selectedLocation?.name ||
		(typeof eventData.location === 'string' && !eventData.location.startsWith('/api/')
			? eventData.location
			: '') ||
		(eventData.locationLabel as string) ||
		(eventData.locationName as string) ||
		(eventData.location_label as string) ||
		(eventData.location_name as string) ||
		(eventData.location && typeof eventData.location === 'object'
			? (eventData.location.label as string) ||
			  (eventData.location.name as string) ||
			  (eventData.location.locationLabel as string) ||
			  (eventData.location.locationName as string) ||
			  ''
			: '');

	const handleBack = () => {
		history.goBack();
	};

	if (isLoadingEvent) {
		return (
			<Box
				sx={{
					backgroundColor: '#f4f6fa',
					minHeight: '100vh',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	if (!eventData) {
		return (
			<Box
				sx={{
					backgroundColor: '#f4f6fa',
					minHeight: '100vh',
					padding: '24px',
				}}
			>
				<Paper sx={{ padding: '24px' }}>
					<h2>Event not found</h2>
					<Button variant="outlined" onClick={handleBack}>
						Back
					</Button>
				</Paper>
			</Box>
		);
	}

	// Format dates/times with fallbacks (API sometimes sends date + time separately)
	const rawStartDate = eventData.startDate || eventData.start;
	const rawEndDate = eventData.endDate || eventData.end;
	const startDateTime =
		combineDateAndTime(rawStartDate, eventData.startTime) ||
		combineDateAndTime(rawStartDate, (eventData as any).start_time) ||
		normalizeDateInput(rawStartDate);
	const endDateTime =
		combineDateAndTime(rawEndDate, eventData.endTime) ||
		combineDateAndTime(rawEndDate, (eventData as any).end_time) ||
		normalizeDateInput(rawEndDate);

	const startMoment = startDateTime ? momentTZ(startDateTime).tz(timezone) : null;
	const endMoment = endDateTime ? momentTZ(endDateTime).tz(timezone) : null;
	const notesValue = eventData.notes || eventData.note || (eventData as any).eventNotes || '';

	const toDocumentRef = (item: any): string => {
		if (!item) return '';
		if (typeof item === 'string') return item.trim();
		if (typeof item === 'object') {
			return String(item['@id'] || item.filePath || item.file_path || item.path || '').trim();
		}
		return '';
	};

	const normalizeDocuments = (raw: any): string[] => {
		if (!raw) return [];
		if (Array.isArray(raw)) return raw.map((x) => toDocumentRef(x)).filter(Boolean);
		if (typeof raw === 'string') {
			return raw
				.split(',')
				.map((x) => x.trim())
				.filter(Boolean);
		}
		return [];
	};

	useEffect(() => {
		let cancelled = false;
		const uploadsBase = (process.env.REACT_APP_IMAGE_URL || '').replace(/\/$/, '');
		const refs = normalizeDocuments((eventData as any)?.documents || (eventData as any)?.banquet_event_order);

		const resolveOne = async (ref: string) => {
			if (/^https?:\/\//i.test(ref)) {
				return { ref, url: ref, label: ref.split('/').pop() || 'Open PDF' };
			}
			if (ref.startsWith('/api/')) {
				try {
					const doc = await getEventDocumentByReference(ref);
					const raw = doc.filePath || doc.file_path || doc.path || '';
					if (!raw) return { ref, url: '', label: 'Preview unavailable' };
					const clean = String(raw).replace(/^\/+/, '').replace(/^uploads\//, '');
					const url = `${uploadsBase}/${clean.startsWith('event_documents/') ? clean : `event_documents/${clean}`}`;
					const label = doc.fileName || doc.file_name || clean.split('/').pop() || 'Open PDF';
					return { ref, url, label };
				} catch {
					return { ref, url: '', label: 'Preview unavailable' };
				}
			}
			// raw file name from API documents[].filePath
			if (!ref.includes('/') && ref.toLowerCase().endsWith('.pdf')) {
				const clean = ref.replace(/^\/+/, '');
				return {
					ref,
					url: `${uploadsBase}/event_documents/${clean}`,
					label: clean,
				};
			}
			const clean = ref.replace(/^\/+/, '').replace(/^uploads\//, '');
			return {
				ref,
				url: `${uploadsBase}/${clean.startsWith('event_documents/') ? clean : `event_documents/${clean}`}`,
				label: clean.split('/').pop() || 'Open PDF',
			};
		};

		const resolveAll = async () => {
			if (!refs.length) {
				if (!cancelled) setDocumentLinks([]);
				return;
			}
			setDocumentsLoading(true);
			const out = await Promise.all(refs.map((r) => resolveOne(r)));
			if (!cancelled) {
				setDocumentLinks(out);
				setDocumentsLoading(false);
			}
		};

		resolveAll();
		return () => {
			cancelled = true;
		};
	}, [eventData]);

	return (
		<Box sx={{ backgroundColor: '#f4f6fa', minHeight: '100vh', py: 3 }}>
			<Box maxWidth="1100px" mx="auto">
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
					<Button
						onClick={handleBack}
						sx={{
							backgroundColor: 'rgb(68, 99, 136)',
							color: '#fff',
							textTransform: 'none',
							fontWeight: 500,
							px: 3,
							py: 0.75,
							'&:hover': { backgroundColor: 'rgb(57, 80, 111)' },
						}}
					>
						Back
					</Button>
				</Box>

				<Paper elevation={1}>
					<Box sx={{ p: 3 }}>
						<h2
							style={{
								fontSize: '1.5rem',
								fontWeight: 600,
								marginBottom: '24px',
								color: '#333',
								marginTop: 0,
							}}
						>
							Event Details
						</h2>

						<Grid container spacing={2}>
							{/* Event Name */}
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									label="Event Name"
									value={eventData.name || eventData.title || ''}
									InputProps={{
										readOnly: true,
									}}
									className={styles.formInput}
								/>
							</Grid>

							{/* Event Type */}
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									label="Event Type"
									value={selectedEventType?.label || ''}
									InputProps={{
										readOnly: true,
									}}
									className={styles.formInput}
								/>
							</Grid>

							{/* Location */}
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									label="Location"
									value={locationDisplayValue}
									InputProps={{
										readOnly: true,
									}}
									className={styles.formInput}
								/>
							</Grid>

							{/* Start Date */}
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									label="Start Date"
									value={startMoment ? startMoment.format('MM/DD/YYYY') : ''}
									InputProps={{
										readOnly: true,
									}}
									className={styles.formInput}
								/>
							</Grid>

							{/* Start Time */}
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									label="Start Time"
									value={startMoment ? startMoment.format('h:mm A') : ''}
									InputProps={{
										readOnly: true,
									}}
									className={styles.formInput}
								/>
							</Grid>

							{/* End Date */}
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									label="End Date"
									value={endMoment ? endMoment.format('MM/DD/YYYY') : ''}
									InputProps={{
										readOnly: true,
									}}
									className={styles.formInput}
								/>
							</Grid>

							{/* End Time */}
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									label="End Time"
									value={endMoment ? endMoment.format('h:mm A') : ''}
									InputProps={{
										readOnly: true,
									}}
									className={styles.formInput}
								/>
							</Grid>

							{/* Number of Attendees */}
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									label="Number of Attendees"
									value={
										eventData.numOfAtendees ||
										eventData.numberOfAttendees ||
										(eventData as any).numOfAttendees ||
										(eventData as any).attendees ||
										''
									}
									InputProps={{
										readOnly: true,
									}}
									className={styles.formInput}
								/>
							</Grid>

							{/* Customer */}
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									label="Customer Name"
									value={customerDisplayName}
									InputProps={{
										readOnly: true,
									}}
									className={styles.formInput}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									label="Customer Email"
									value={
										selectedCustomer?.email ||
										(eventData as any).organizer_email ||
										(eventData as any).customerEmail ||
										''
									}
									InputProps={{
										readOnly: true,
									}}
									className={styles.formInput}
								/>
							</Grid>

							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									label="Customer Phone Number"
									value={
										selectedCustomer?.phone ||
										(eventData as any).organizer_phone_number ||
										(eventData as any).customerPhoneNumber ||
										''
									}
									InputProps={{
										readOnly: true,
									}}
									className={styles.formInput}
								/>
							</Grid>

							{/* Tax Exempt ID */}
							<Grid item xs={12} sm={6}>
								<TextField
									fullWidth
									label="Tax Exempt ID"
									value={
										eventData.taxExemptId ||
										(eventData as any).tax_exempt_id ||
										selectedCustomer?.taxExemptId ||
										''
									}
									InputProps={{
										readOnly: true,
									}}
									className={styles.formInput}
								/>
							</Grid>

							{/* Notes */}
							<Grid item xs={12}>
								<TextField
									fullWidth
									label="Notes"
									value={notesValue || ''}
									multiline
									rows={4}
									InputProps={{
										readOnly: true,
									}}
									className={styles.formInput}
								/>
							</Grid>

							{/* Uploaded Documents (view-only list, no template selector) */}
							<Grid item xs={12}>
								<Typography
									variant="h6"
									sx={{ fontFamily: 'Gotham', color: 'rgb(68, 99, 136)', mb: 1, fontWeight: 400 }}
								>
									Uploaded Documents
								</Typography>
								{documentsLoading ? (
									<CircularProgress size={20} />
								) : documentLinks.length > 0 ? (
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
										{documentLinks.map((doc, idx) => (
											<Box key={`${doc.ref}-${idx}`} sx={{ p: 1, border: '1px solid #dbe3ee', borderRadius: '6px' }}>
												{doc.url ? (
													<a href={doc.url} target="_blank" rel="noopener noreferrer">
														{doc.label}
													</a>
												) : (
													<Typography variant="body2" color="text.secondary">
														{doc.label}
													</Typography>
												)}
											</Box>
										))}
									</Box>
								) : (
									<Typography variant="body2" color="text.secondary">
										No uploaded documents
									</Typography>
								)}
							</Grid>

							{/* Event Staffing */}
							{(((eventData as any).eventStaffing || (eventData as any).event_staffing) ?? []).length > 0 && (
								<Grid item xs={12}>
									<Typography
										variant="h6"
										sx={{ fontFamily: 'Gotham', color: 'rgb(68, 99, 136)', mb: 2, fontWeight: 400 }}
									>
										Required Staffing
									</Typography>
									{(((eventData as any).eventStaffing || (eventData as any).event_staffing) ?? []).map(
										(staff: any, index: number) => {
										const jobCodeId = staff.jobCode ? extractIdFromIri(staff.jobCode) : null;
										const jobCodeItem = sortedJobCodesData.find((jc: any) => jc.id === jobCodeId);

										return (
											<Grid container spacing={2} key={index} sx={{ mb: 2 }}>
												<Grid item xs={12} sm={6}>
													<TextField
														fullWidth
														label="Job Code"
														value={jobCodeItem ? `${jobCodeItem.jobNumber} - ${jobCodeItem.label}` : ''}
														InputProps={{
															readOnly: true,
														}}
														className={styles.formInput}
													/>
												</Grid>
												<Grid item xs={12} sm={6}>
													<TextField
														fullWidth
														label="Number Of Additional Staff"
														value={staff.numberOfAdditionalStaff || 0}
														InputProps={{
															readOnly: true,
														}}
														className={styles.formInput}
													/>
												</Grid>
											</Grid>
										);
									})}
								</Grid>
							)}
						</Grid>
					</Box>
				</Paper>
			</Box>
		</Box>
	);
};

export default EventDetails;
